import { ChangeEvent, useCallback, useContext } from 'react';
import { FUNDS_MEASUREMENT_DATE } from 'common/constants/funds';
import { MEASUREMENT_DATE_ACTION_ADD, MEASUREMENT_DATE_ACTION_DELETE } from 'common/constants/measurement-date';
import { FUNDS_MEASUREMENT_DATE_FILTER } from 'common/constants/pageFilters';
import { MeasurementDate, MeasurementDates, SelectedMeasurementDate } from 'common/types/measurementDate';
import { LayoutContextValues } from 'common/types/store';
import { LayoutContext } from 'context';
import { useDateQueryParam } from 'hooks';
import { onMeasurementDateChange } from 'pages/Funds/utils';
import { formatMeasurementDates, getArrayValue, getMeasurementDate } from 'utilities';
import { SetFiltersHook, UseSetFilters } from './types';

const useSetFilters: UseSetFilters = () => {
  const { setPageFilters, commonMeasurementDate, setCommonMeasurementDate } = useContext(
    LayoutContext
  ) as unknown as LayoutContextValues;

  const { setDateQueryParam } = useDateQueryParam();

  const getSelectedValue = useCallback(
    (
      selectedMeasurementDate: SelectedMeasurementDate,
      measurementDates: MeasurementDates,
      openedMeasurementDate: MeasurementDate,
    ): string | number | undefined => {
      if (selectedMeasurementDate?.id === null || selectedMeasurementDate?.id === undefined) {
        return selectedMeasurementDate?.name ? 'most-recent' : getMeasurementDate({
          commonMeasurementDate,
          fallbackValue: openedMeasurementDate?.id as number,
          measurementDates,
        }) as number;
      }

      return selectedMeasurementDate.id;
    },
    [commonMeasurementDate]
  );

  const setFilters: SetFiltersHook['setFilters'] = useCallback(
    params => {
      const {
        measurementDates,
        openedMeasurementDate,
        openMeasurementDateModal,
        selectedMeasurementDate,
        setSelectedMeasurementDate,
      } = params;

      // Handle "Most Recent" which has null ID - use a special identifier string
      // Otherwise use the numeric ID or fallback to opened measurement date
      const selectedValue = getSelectedValue(selectedMeasurementDate, measurementDates, openedMeasurementDate);

      setPageFilters?.([
        {
          addOption: () => openMeasurementDateModal(MEASUREMENT_DATE_ACTION_ADD),
          deleteOption: () => openMeasurementDateModal(MEASUREMENT_DATE_ACTION_DELETE),
          id: FUNDS_MEASUREMENT_DATE_FILTER,
          handler: (event: ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
            onMeasurementDateChange({
              event: event as ChangeEvent<HTMLSelectElement>,
              measurementDates,
              setCommonMeasurementDate,
              setSelectedMeasurementDate,
              setDateQueryParam,
            });
          },
          options: formatMeasurementDates(getArrayValue(measurementDates)),
          selectedValue,
          title: FUNDS_MEASUREMENT_DATE,
        },
      ]);
    },
    [getSelectedValue, setPageFilters, setCommonMeasurementDate, setDateQueryParam]
  );

  return {
    setFilters,
  };
};

export default useSetFilters;
